import { forwardRef } from 'react'
import { cn } from '../../lib/cn'

type ChipVariant = 'default' | 'ok' | 'info' | 'warn' | 'danger' | 'intent'

interface ChipProps {
	variant?: ChipVariant
	children: React.ReactNode
	className?: string
}

/**
 * Chip — financial-grade status tag.
 *
 *   `default`  — slate-tinted neutral, for category labels
 *   `ok`       — emerald (semantic green for positive states)
 *   `info`     — cyan (matches the ring / signature accent)
 *   `warn`     — amber
 *   `danger`   — red
 *   `intent`   — stronger cyan with border, for "live" / "primary status"
 *
 * Subtle borders (`border-X-500/30`) give tags slight definition without
 * looking heavy — a pro tell vs the previous borderless-fill design.
 */
const Chip = forwardRef<HTMLSpanElement, ChipProps>(
	({ variant = 'default', children, className }, ref) => {
		return (
			<span
				ref={ref}
				className={cn(
					'inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium leading-relaxed',
					variant === 'default' &&
						'bg-secondary text-muted-foreground border border-border',
					variant === 'ok' &&
						'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30',
					variant === 'info' &&
						'bg-cyan-500/10 text-cyan-300 border border-cyan-500/30',
					variant === 'warn' &&
						'bg-amber-500/10 text-amber-300 border border-amber-500/30',
					variant === 'danger' &&
						'bg-red-500/10 text-red-300 border border-red-500/30',
					variant === 'intent' &&
						'bg-cyan-500/15 text-cyan-200 border border-cyan-400/50',
					className,
				)}
			>
				{children}
			</span>
		)
	},
)

Chip.displayName = 'Chip'

export type { ChipVariant }
export { Chip }
